import { useCallback } from 'react';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import type { Market } from '@//config/domain-locale-map';
import { useLegacyTracking } from '@//providers/LegacyTrackingProvider';
import { useMarket } from '@//providers/MarketSettingsProvider';
import type { LegacyTrackInsuranceInterestedEventData } from './legacy-types';
import { useLegacyTrackShownInterest } from './useLegacyTrackShownInterest';
import { mapElasticVehicleToLegacyItem } from './utils';

interface UseLegacyTrackInsuranceInterestedVariables {
  vehicle: ElasticVehicle;
  information: LegacyTrackInsuranceInterestedEventData['information'];
}

export const createLegacyTrackInsuranceInterested = (
  market: Market,
  variables: UseLegacyTrackInsuranceInterestedVariables,
): LegacyTrackInsuranceInterestedEventData => {
  const eventData: LegacyTrackInsuranceInterestedEventData = {
    eventType: 'microConversion',
    conversionType: 'insuranceInterest',
    item: mapElasticVehicleToLegacyItem(market, variables.vehicle),
    information: variables.information,
    pageType: 'product page',
  };

  return eventData;
};

export const useLegacyTrackInsuranceInterested = () => {
  const market = useMarket();
  const { track } = useLegacyTracking();
  const { trackLegacyShownInterest } = useLegacyTrackShownInterest();

  const trackLegacyInsuranceInterested = useCallback(
    (variables: UseLegacyTrackInsuranceInterestedVariables) => {
      trackLegacyShownInterest(variables.vehicle);
      track({
        event: 'microConversion',
        data: createLegacyTrackInsuranceInterested(market, variables),
      });
    },
    [market, track],
  );

  return {
    trackLegacyInsuranceInterested,
  };
};
